import React, {Fragment, useState} from 'react';
import {Popover, Transition} from '@headlessui/react';
import {
  Bars3Icon,
  ShoppingBagIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {Text} from '~/components/elements';
import {Image, Link, useCart} from '@shopify/hydrogen';
import clsx from 'clsx';
import {LockClosedIcon} from '@heroicons/react/20/solid';

// import {CartDrawer} from './CartDrawer.client';
// import {MenuDrawer} from './MenuDrawer.client';
// import {useDrawer} from './Drawer.client';

// TODO: Don't just leave these components laying around
// copy cart drawer?

import type {EnhancedMenu} from '~/lib/utils';

const navigation = {
  categories: [
    {
      name: 'Women',
      featured: [
        {
          name: 'New Arrivals',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-category-01.jpg',
          imageAlt:
            'Models sitting back to back, wearing Basic Tee in black and bone.',
        },
        {
          name: 'Basic Tees',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-category-02.jpg',
          imageAlt:
            'Close up of Basic Tee fall bundle with off-white, ochre, olive, and black tees.',
        },
        {
          name: 'Accessories',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-category-03.jpg',
          imageAlt:
            'Model wearing minimalist watch with black wristband and white watch face.',
        },
        {
          name: 'Carry',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-category-04.jpg',
          imageAlt:
            'Model opening tan leather long wallet with credit card pockets and cash pouch.',
        },
      ],
    },
    {
      name: 'Men',
      featured: [
        {
          name: 'New Arrivals',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-01-men-category-01.jpg',
          imageAlt:
            'Hats and sweaters on wood shelves next to various colors of t-shirts on hangers.',
        },
        {
          name: 'Basic Tees',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-01-men-category-02.jpg',
          imageAlt: 'Model wearing light heather gray t-shirt.',
        },
        {
          name: 'Accessories',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-01-men-category-03.jpg',
          imageAlt:
            'Grey 6-panel baseball hat with black brim, black mountain graphic on front, and light heather gray body.',
        },
        {
          name: 'Carry',
          href: '#',
          imageSrc:
            'https://tailwindui.com/img/ecommerce-images/mega-menu-01-men-category-04.jpg',
          imageAlt:
            'Model putting folded cash into slim card holder olive leather wallet with hand stitching.',
        },
      ],
    },
  ],
  pages: [
    {name: 'Company', href: '#'},
    {name: 'Stores', href: '#'},
  ],
};

export function Header({
  title,
  menu,
  authenticated,
}: {
  title: string;
  menu?: EnhancedMenu;
  authenticated: React.ReactNode;
}) {
  const [isFeatureHeader, setIsFeatureHeader] = useState(false);

  return (
    <header className="relative z-10">
      <nav aria-label="Top">
        {/* Top navigation */}
        <div className="bg-accent">
          <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            {/* Enter access code */}
            {/* <button
              className="flex items-center space-x-2 text-white/80 hover:text-white"
              onClick={() => {
                window.dispatchEvent(new Event('access-code.toggle'));
              }}
            >
              <LockClosedIcon className="h-5 w-5" aria-hidden="true" />
              <Text color="contrast" className="font-semibold" size="fine">
                Enter access code
              </Text>
            </button> */}

            <div></div>

            <div className="flex items-center space-x-3">
              <p className="text-sm font-medium text-white hover:text-gray-100">
                {authenticated}
              </p>
              <Link to={'/account'}>
                <span className="sr-only">View my account details</span>
                <UserCircleIcon
                  className="h-5 w-5 text-white"
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>
        </div>

        {/* Secondary navigation */}
        <div
          // onClick={() => setIsFeatureHeader(!isFeatureHeader)}
          className={clsx([
            isFeatureHeader
              ? 'absolute w-full text-white'
              : 'bg-white text-gray-900 shadow-lg',
          ])}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div>
              <div className="flex h-16 items-center justify-between">
                {/* Logo (lg+) */}
                <div className="hidden lg:flex lg:flex-1 lg:items-center">
                  <Link to={'/'}>
                    <span className="sr-only">{title}</span>
                    <Image
                      width={600}
                      height={240}
                      className="h-14 w-auto"
                      src="/images/believe-photography-logo.png"
                      alt={title}
                    />
                  </Link>
                </div>

                <div className="hidden h-full lg:flex">
                  {/* Flyout menus */}
                  <Popover.Group className="inset-x-0 bottom-0 px-4">
                    <div className="flex h-full items-center justify-center space-x-8">
                      {/* TODO: make sweet ass menu */}
                      {navigation.categoriesRemoved?.map((category) => (
                        <Popover key={category.name} className="flex">
                          {({open}) => (
                            <>
                              <div className="relative flex">
                                <Popover.Button className="relative z-10 flex items-center justify-center text-sm font-medium text-gray-700 transition-colors duration-200 ease-out">
                                  {category.name}
                                  <span
                                    className={clsx(
                                      open ? 'bg-white' : '',
                                      'absolute inset-x-0 -bottom-px h-0.5 transition duration-200 ease-out',
                                    )}
                                    aria-hidden="true"
                                  />
                                </Popover.Button>
                              </div>

                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-200"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition ease-in duration-150"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                              >
                                <Popover.Panel className="absolute inset-x-0 top-full text-sm text-gray-500">
                                  {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
                                  <div
                                    className="absolute inset-0 top-1/2 bg-white shadow"
                                    aria-hidden="true"
                                  />

                                  <div className="relative bg-white">
                                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                                      <div className="grid grid-cols-4 gap-y-10 gap-x-8 py-16">
                                        {category.featured.map((item) => (
                                          <div
                                            key={item.name}
                                            className="group relative"
                                          >
                                            <div className="aspect-square overflow-hidden rounded-md bg-gray-100 group-hover:opacity-75">
                                              <img
                                                src={item.imageSrc}
                                                alt={item.imageAlt}
                                                className="object-cover object-center"
                                              />
                                            </div>
                                            <a
                                              href={item.href}
                                              className="mt-4 block font-medium text-gray-900"
                                            >
                                              <span
                                                className="absolute inset-0 z-10"
                                                aria-hidden="true"
                                              />
                                              {item.name}
                                            </a>
                                            <p
                                              aria-hidden="true"
                                              className="mt-1"
                                            >
                                              Shop now
                                            </p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </Popover.Panel>
                              </Transition>
                            </>
                          )}
                        </Popover>
                      ))}

                      {(menu?.items || []).map((item) => (
                        <Link
                          key={item.id}
                          to={item.to}
                          target={item.target}
                          className="inline-block rounded-lg py-2 px-3 text-sm text-slate-800 hover:bg-slate-100 hover:text-slate-900"
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </Popover.Group>
                </div>

                {/* Mobile menu and search (lg-) */}
                <div className="flex flex-1 items-center lg:hidden">
                  <button
                    type="button"
                    className="-ml-2 rounded bg-white/30 p-2"
                    onClick={() => {
                      window.dispatchEvent(new Event('menu.toggle'));
                    }}
                  >
                    <span className="sr-only">Open menu</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  </button>

                  {/* Search */}
                  {/* <a href="#" className="ml-2 p-2">
                    <span className="sr-only">Search</span>
                    <MagnifyingGlassIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                  </a> */}
                </div>

                {/* Logo (lg-) */}
                <Link to={'/'} className="lg:hidden">
                  <span className="sr-only">{title}</span>
                  <Image
                    alt={title}
                    className="h-14 w-auto"
                    src="/images/believe-photography-logo.png"
                    height={240}
                    loading={'lazy'}
                    sizes="(min-width: 768px) 50vw, 100vw"
                    width={600}
                    loaderOptions={{
                      scale: 2,
                      crop: 'center',
                    }}
                  />
                </Link>

                <div className="flex flex-1 items-center justify-end">
                  {/* <a href="#" className="hidden text-sm font-medium lg:block">
                    Search
                  </a> */}

                  <div className="flex items-center lg:ml-8">
                    {/* Help */}
                    {/* <a href="#" className="p-2 lg:hidden">
                      <span className="sr-only">Help</span>
                      <QuestionMarkCircleIcon
                        className="h-6 w-6"
                        aria-hidden="true"
                      />
                    </a> */}
                    {/* <a href="#" className="hidden text-sm font-medium lg:block">
                      Help
                    </a> */}

                    {/* Cart */}
                    <div className="ml-4 flow-root lg:ml-8">
                      <Link
                        to={'/cart'}
                        className="group -m-2 flex items-center p-2"
                      >
                        <ShoppingBagIcon
                          className="h-6 w-6 flex-shrink-0"
                          aria-hidden="true"
                        />
                        <CartBadge />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

function CartBadge() {
  const {totalQuantity} = useCart();

  if (totalQuantity < 1) {
    return null;
  }

  return (
    <>
      <span className="ml-2 text-sm font-medium ">{totalQuantity}</span>
      <span className="sr-only">items in cart</span>
    </>
  );
}
